import type { AccountRole } from '~/types'
import { accountRoles } from '~/authorization'

/*
 * Router Permissions
 * Which routes can roles access within the application?
 * Adding "no-role" will allow every role to use the rout
 *
 * WildCard Paths: Must end with a `/*` to allow any of the sub pages to be accessed
 */
const PERMISSIONS_BY_PATH = {
  // Public
  '/': ['no-role'],
  '/auth/logout': accountRoles.options,
  '/imprint': ['no-role'],
  '/privacy': ['no-role'],
  '/accessibility': ['no-role'],
  '/self-service/*': ['person'],

  // Administration
  '/administration/users/city': ['admin', 'infection-protection-manager'],
  '/administration/templates/messages': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'heat-protection-clerk', 'heat-protection-specialist'],
  '/administration/templates/messages/update/*': ['admin', 'infection-protection-manager', 'heat-protection-clerk', 'heat-protection-specialist'],
  '/administration/templates/documents': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'heat-protection-clerk', 'heat-protection-specialist'],
  '/administration/bearerTokens': ['admin', 'infection-protection-manager'],

  // Accounts
  '/users/citizens': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/users/citizens/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/users/details/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Institutions
  '/institutions': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/institutions/details/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Infection protection management
  '/infection-protection/management': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/disease': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/disease/create': ['admin', 'infection-protection-manager'],
  '/infection-protection/management/disease/update/*': ['admin', 'infection-protection-manager'],
  '/infection-protection/management/vaccine': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/predisposition': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/symptoms': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/test-material': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/teams': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/institution-types': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/infection-protection/management/import-logs': ['admin', 'infection-protection-manager'],

  // Reports
  '/reports': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  '/reports/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],

  // Cases
  '/cases': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/cases/filter/highrisk': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/cases/create': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/cases/update/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/cases/details/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/cases/planned-notifications': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Outbreaks
  '/outbreaks': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  '/outbreaks/details/*': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Heat Protection
  '/heat-protection': ['admin', 'heat-protection-clerk', 'heat-protection-specialist'],
  '/heat-protection/*': ['admin', 'heat-protection-clerk', 'heat-protection-specialist'],
} as const satisfies Record<string, AccountRole[]>
export type PathPermissions = keyof typeof PERMISSIONS_BY_PATH

/*
 * Page Permissions
 * Extra permissions that can be reused in the back and frontend to specify certain features.
 */

const PAGE_PERMISSIONS = {
  // Accounts
  'account.role.update': ['admin', 'infection-protection-manager'],
  'account.status.update': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  'account.tab.cases': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'person'],
  'account.tab.immunizations': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'immunization.create.others': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'account.tab.documents': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'person'],
  'account.tab.events': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'account.tab.institutions': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'account.delete': ['admin', 'infection-protection-manager'],
  'account.merge': ['admin', 'infection-protection-manager'],
  'account.create.subperson': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'account.create.empty': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'account.update.identifier': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'person.export': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Notes
  'notes.view': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'notes.create': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'notes.update.others': ['admin', 'infection-protection-manager'],
  'notes.delete.others': ['admin', 'infection-protection-manager'],

  // Administration
  'documents.template.create': ['admin', 'infection-protection-manager', 'heat-protection-clerk', 'heat-protection-specialist'],
  'documents.template.update': ['admin', 'infection-protection-manager', 'heat-protection-clerk', 'heat-protection-specialist'],
  'teams.create': ['admin', 'infection-protection-manager'],
  'teams.update': ['admin', 'infection-protection-manager'],
  'messages.template.create': ['admin', 'infection-protection-manager', 'heat-protection-clerk', 'heat-protection-specialist'],
  'messages.send': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'heat-protection-clerk', 'heat-protection-specialist'],

  // Institutions
  'institutions.members.update': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'institutions.members.delete': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'institution'],
  'institutions.members.seeAll': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'institutions.tab.cases': ['admin', 'infection-protection-manager', 'infection-protection-clerk', 'infection-protection-specialist', 'institution'],
  'institutions.tab.documents': ['admin', 'infection-protection-manager', 'infection-protection-clerk', 'infection-protection-specialist', 'institution'],
  'institutions.csv.import': ['admin', 'infection-protection-manager'],

  // Cases
  'case.team.update': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'case.tab.contactCases': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'case.tab.notification': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'case.tab.diseaseTests': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'case.csv.import': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
  'case.export': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'case.status.update': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'case.closed.update': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  'symptomDiaryEntries.viewCreator': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'diseaseVariant.use': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'hospitalizations.tab': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Outbreaks
  'outbreaks.state.update': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],

  // Documents
  'documents.create.others': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'heat-protection-clerk', 'heat-protection-specialist'],
  'documents.sendPostal': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk'],
  'documents.viewHidden': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'heat-protection-clerk', 'heat-protection-specialist'],
  'documents.delete': ['admin', 'infection-protection-manager'],
  'documents.generate': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'heat-protection-clerk', 'heat-protection-specialist'],

  // Filter Templates
  'filter.template.create': ['admin', 'infection-protection-manager', 'heat-protection-clerk', 'heat-protection-specialist'],
  'filter.template.delete': ['admin', 'infection-protection-manager', 'heat-protection-clerk', 'heat-protection-specialist'],
  'filter.use': ['admin', 'infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'heat-protection-clerk', 'heat-protection-specialist'],

  // DEMIS
  'demis.sendReport': ['admin', 'infection-protection-manager', 'infection-protection-specialist'],
} as const satisfies Record<string, AccountRole[]>
export type PagePermissions = keyof typeof PAGE_PERMISSIONS

/*
 * Role updating Permissions
 * Which roles can a role update an account to?
 */

export const allowedRolesToSet: Partial<Record<AccountRole, AccountRole[]>> = {
  'admin': accountRoles.options,
  'infection-protection-manager': ['infection-protection-manager', 'infection-protection-specialist', 'infection-protection-clerk', 'person'],
  'infection-protection-specialist': ['person'],
  'infection-protection-clerk': ['person'],
  'institution': ['institution'],
}

/*
 * Authorization functions
 * Determine is a role can access a certain page or feature
 */

export function roleToSet(payload: { role: AccountRole, previousRole?: AccountRole }, ownRole: AccountRole): AccountRole {
  if (payload.previousRole && payload.previousRole === 'institution') {
    return 'institution'
  }
  // If the role has not changed, allow the update
  if (payload.previousRole && payload.role === payload.previousRole) {
    return payload.role
  }

  // Check if the account can set this permission, otherwise fallback to the previous role or person
  const fallbackRole = payload.previousRole ?? 'person'
  return allowedRolesToSet[ownRole]?.includes(payload.role) ? payload.role : fallbackRole
}

function can(key: string, role: AccountRole, allowedRoles: AccountRole[]) {
  if (!allowedRoles) {
    console.warn(`can: No rules found for this key, denying access. Key is: ${key}`)
    return false
  }

  // no-role means that even people without an account can use this
  if (allowedRoles.includes('no-role')) {
    return true
  }

  // Admins can use everything
  if (role === 'admin') {
    return true
  }

  return allowedRoles.includes(role)
}

export const canUse = (role: AccountRole = 'no-role', key: PagePermissions) => can(key, role, PAGE_PERMISSIONS[key] ?? [])
export const canAccess = (role: AccountRole = 'no-role', key: PathPermissions | string) => {
  const staticPermission = PERMISSIONS_BY_PATH[key as PathPermissions]
  if (staticPermission) {
    return can(key, role, staticPermission)
  }

  const wildCardRouteKeys = Object.keys(PERMISSIONS_BY_PATH).filter(path => path.endsWith('/*'))
  const wildCardPermission = wildCardRouteKeys.filter(path => key.startsWith(path.replace('*', ''))).flatMap(path => PERMISSIONS_BY_PATH[path as PathPermissions])

  return can(key, role, wildCardPermission)
}
